import React from 'react';
import cs from 'classnames';
import styles from './Buttons.module.scss';

const containedButtonStyle = {
  purple: styles.containedPurple,
  dark: styles.containedDark,
  red: styles.containedRed,
  default: styles.default,
};

type TContainedBtn = {
  onClick?: (e?: any) => void;
  children: React.ReactNode;
  kind?: 'purple' | 'dark' | 'red' | 'default';
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  className?: string;
  hidden?: boolean;
  style?: any;
  id?: string;
  ariaLabel?: string;
};

export const ContainedButton: React.FC<TContainedBtn> = ({
  onClick,
  children,
  hidden = false,
  kind = 'purple',
  type = 'button',
  disabled = false,
  className,
  style,
  id,
  ariaLabel,
}) => {
  return (
    /* eslint-disable react/button-has-type */
    <button
      disabled={disabled}
      type={type}
      className={cs(styles.btn, containedButtonStyle[kind], disabled ? styles.disabled : '', className)}
      onClick={onClick}
      hidden={hidden}
      style={style && { ...style }}
      id={id}
      aria-label={ariaLabel}
    >
      {children}
    </button>
    /* eslint-enable react/button-has-type */
  );
};

type TOutlinedBtn = {
  onClick?: (e?: any) => void;
  children: React.ReactNode;
  kind?: 'purple' | 'gray' | 'default';
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  hidden?: boolean;
  className?: string;
  style?: any;
};

const outlinedButtonStyle = {
  purple: styles.outlinedPurple,
  gray: styles.outlinedGray,
  default: styles.default,
};

export const OutlinedButton: React.FC<TOutlinedBtn> = ({
  onClick,
  children,
  kind = 'purple',
  type = 'button',
  disabled = false,
  hidden = false,
  className,
  style,
}) => {
  return (
    /* eslint-disable react/button-has-type */
    <button
      type={type}
      disabled={disabled}
      className={cs(styles.btn, outlinedButtonStyle[kind], disabled ? styles.disabled : '', className)}
      onClick={onClick}
      hidden={hidden}
      style={style && { ...style }}
    >
      {children}
    </button>
    /* eslint-enable react/button-has-type */
  );
};

type TGrayButton = {
  onClick?: (e?: any) => void;
  children: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  className?: string;
};

export const GrayButton: React.FC<TGrayButton> = ({ onClick, children, type = 'button', disabled = false, className }) => {
  return (
    /* eslint-disable react/button-has-type */
    <button
      disabled={disabled}
      type={type}
      className={cs(styles.btn, styles.grayButton, disabled ? styles.disabled : '', className)}
      onClick={onClick}
    >
      {children}
    </button>
    /* eslint-enable react/button-has-type */
  );
};

type TRolesBtn = {
  onClick?: (e?: any) => void;
  children: React.ReactNode;
  kind?: 'blue' | 'orange' | 'green' | 'gray' | 'white' | 'purple' | 'red' | 'mint' | 'darkPurple';
  hover?: 'blue' | 'orange' | 'green' | 'none' | 'white' | 'purple' | 'red' | 'mint' | 'darkPurple';
  type?: 'button';
  customStyles?: object;
  disable?: boolean;
};

const rolesButtonStyle = {
  blue: styles.rolesBlue,
  orange: styles.rolesOrange,
  green: styles.rolesGreen,
  gray: styles.rolesGray,
  white: styles.rolesWhite,
  purple: styles.rolesPurple,
  darkPurple: styles.rolesDarkPurple,
  red: styles.rolesRed,
  mint: styles.rolesMint,
};

const rolesButtonHoverStyle = {
  blue: styles.rolesBlueHover,
  orange: styles.rolesOrangeHover,
  green: styles.rolesGreenHover,
  white: styles.rolesWhiteHover,
  purple: styles.rolesPurpleHover,
  darkPurple: styles.rolesDarkPurple,
  red: styles.rolesRedHover,
  mint: styles.rolesMintHover,
  none: null,
};

export const RolesButton: React.FC<TRolesBtn> = ({
  onClick,
  children,
  kind = 'blue',
  hover = null,
  type = 'button',
  customStyles,
  disable,
}) => {
  return (
    /* eslint-disable react/button-has-type */
    <button
      type={type}
      className={cs(rolesButtonStyle[kind], hover && !disable ? rolesButtonHoverStyle[hover] : null, styles.btn)}
      onClick={onClick}
      style={customStyles}
      disabled={disable}
    >
      {children}
    </button>
    /* eslint-enable react/button-has-type */
  );
};
